import React, { useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import NavigationTreeNode from '../NavigationTreeNode';
import NavigationTreeNodeContent, { calculateRowHeight } from '../NavigationTreeNodeContent';
import SortableTree from '../SortableTree';
import deepEdit from './deepEdit';
import deepRemove from './deepRemove';
import deepReorder from './deepReorder';
import toggleButton from './toggleButton';
import treeDataToNavigation from './treeDataToNavigation';
import canDrag from './canDrag';
import canDrop from './canDrop';

const propTypes = {
  className: PropTypes.string,
  navigation: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

const defaultProps = {
  className: undefined,
};

const NavigationTree = ({
  className,
  navigation,
  onChange,
}) => {
  const [expandedIds, setExpandedIds] = useState([]);

  const onEdit = (navigationItem) => {
    const newNavigation = deepEdit(navigation, navigationItem.id, oldNavigationItem => ({
      ...oldNavigationItem,
      ...navigationItem,
    }));

    onChange(newNavigation);
  };

  const onMoveToBottom = (id) => {
    const newNavigation = deepReorder(navigation, id, (navigationItem, navigationItems) => {
      let buttonCount = 0;
      const hasSecondaryButton = navigationItems.some(item => item.isSecondary);
      const hasPrimaryButton = navigationItems.some(item => item.isPrimary);

      if (hasSecondaryButton) { buttonCount += 1; }
      if (hasPrimaryButton) { buttonCount += 1; }

      const buttons = navigationItems.splice(navigationItems.length - buttonCount);
      return [
        ...navigationItems,
        navigationItem,
        ...buttons,
      ];
    });

    onChange(newNavigation);
  };

  const onMoveToTop = (id) => {
    const newNavigation = deepReorder(navigation, id, (navigationItem, navigationItems) => ([
      navigationItem,
      ...navigationItems,
    ]));

    onChange(newNavigation);
  };

  const onRemove = (id) => {
    const newNavigation = deepRemove(navigation, id);

    onChange(newNavigation);
  };

  const onToggleButton = (item, toggle, attribute) => {
    const newNavigation = toggleButton({ toggle, attribute, navigation, item });

    onChange(newNavigation);
  };

  const navigationToTreeData = items => items.map((navigationItem, idx) => {
    const { navigationItems } = navigationItem;

    const hasSecondaryButton = items.some(item => item.isSecondary);
    const hasPrimaryButton = items.some(item => item.isPrimary);
    const nextItem = items[idx + 1];
    const canMoveToBottom = nextItem && !nextItem.isSecondary && !nextItem.isPrimary;

    return {
      canMoveToBottom,
      children: navigationItems && navigationToTreeData(navigationItems),
      expanded: expandedIds.includes(navigationItem.id),
      hasPrimaryButton,
      hasSecondaryButton,
      navigationItem,
      onEdit,
      onMoveToBottom,
      onMoveToTop,
      onRemove,
      onToggleButton,
    };
  });

  const onTreeChange = (tree) => {
    const [newNavigation, newExpandedIds] = treeDataToNavigation(tree);

    setExpandedIds(newExpandedIds);
    onChange(newNavigation);
  };

  const onRowHeightCalculate = ({ node }) => calculateRowHeight(
    node.navigationItem,
  );

  return (
    <div className={className}>
      <SortableTree
        maxDepth={4}
        canDrag={canDrag}
        canDrop={args => canDrop({ ...args, navigation })}
        nodeComponentClass={NavigationTreeNode}
        nodeContentComponentClass={NavigationTreeNodeContent}
        onChange={onTreeChange}
        rowHeight={onRowHeightCalculate}
        treeData={navigationToTreeData(navigation)}
      />
    </div>
  );
};

NavigationTree.propTypes = propTypes;
NavigationTree.defaultProps = defaultProps;

NavigationTree.fragments = {
  navigationItem: gql`
    fragment NavigationTreeNavigationItem on NavigationItem {
      ...NavigationTreeNodeContentNavigationItem
    }

    ${NavigationTreeNodeContent.fragments.navigationItem}
  `,
  page: gql`
    fragment NavigationTreePage on Page {
      ...NavigationTreeNodeContentPage
    }

    ${NavigationTreeNodeContent.fragments.page}
  `,
};

export default NavigationTree;
